import { useFragment } from '@apollo/client';
import { IconButton } from '@synoptic/ui-kit/button/icon-button.js';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from '@synoptic/ui-kit/dropdown-menu/dropdown-menu.js';
import { LinkIcon } from '@synoptic/ui-kit/icons/react/link.js';
import { MoreHorizIcon } from '@synoptic/ui-kit/icons/react/more-horiz.js';
import { UserPlusIcon } from '@synoptic/ui-kit/icons/react/user-plus.js';
import { UserXmarkIcon } from '@synoptic/ui-kit/icons/react/user-xmark.js';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.js';
import { PostCardAuthorFragmentFragment } from '@/__generated__/graphql.ts';
import { paths } from '@/routes-utils/paths.ts';
import { useAuthInfo } from '@/routes/_app/use-auth-info.ts';
import { copyUrlToClipboard } from '@/utils/copy-url-to-clipboard.ts';
import {
  UserFollowingFragment,
  useFollow,
  useUnfollow,
} from '../../toggle-follow/use-toggle-follow.ts';
import { postCardActionsMenu } from '../post-card.css.ts';
import { actionFollow } from './post-card-actions-menu.css.ts';

export const PostCardActionsMenu = ({
  id,
  author,
}: {
  id: string;
  author: PostCardAuthorFragmentFragment;
}) => {
  const me = useAuthInfo();
  const toast = useToast();

  const { data } = useFragment({
    fragment: UserFollowingFragment,
    from: {
      __typename: 'User',
      id: author.id,
    },
  });

  const [follow] = useFollow(author.id);
  const [unfollow] = useUnfollow(author.id);

  const isAuthor = author.id === me.id;

  const actions = [
    <DropdownMenuItem
      key="copy-link"
      icon={<LinkIcon />}
      onSelect={() => {
        copyUrlToClipboard(paths.makePost(id));
        toast.info({ title: 'Link copied to clipboard' });
      }}
    >
      Copy link to post
    </DropdownMenuItem>,
  ];

  if (!isAuthor) {
    const followAction = data.following ? (
      <DropdownMenuItem
        key="follow"
        icon={<UserXmarkIcon />}
        onSelect={() =>
          unfollow().then(() => {
            toast.info({
              title: `You unfollowed @${author.username}`,
            });
          })
        }
      >
        <span className={actionFollow}>Unfollow @{author.username}</span>
      </DropdownMenuItem>
    ) : (
      <DropdownMenuItem
        key="follow"
        icon={<UserPlusIcon />}
        onSelect={() => {
          follow().then(() => {
            toast.info({ title: `You followed @${author.username}` });
          });
        }}
      >
        <span className={actionFollow}>Follow @{author.username}</span>
      </DropdownMenuItem>
    );

    actions.push(followAction);
  }

  return (
    <div className={postCardActionsMenu} onClick={(e) => e.stopPropagation()}>
      <DropdownMenuRoot>
        <DropdownMenuTrigger>
          <IconButton
            size="small"
            variant="ghost"
            aria-label="Post actions menu"
          >
            <MoreHorizIcon style={{ height: 24, width: 24 }} />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent>{actions}</DropdownMenuContent>
      </DropdownMenuRoot>
    </div>
  );
};
